import {clamp, sortBy} from 'remeda'

const isOppositeSign = (a: number, b: number) => (a > 0 && b < 0) || (a < 0 && b > 0)

/** iterates to find a value for x that returns the desired result within a tolerance */
export const solveForV = <T extends {result: number | undefined}>(
  fx: (v: number) => T,
  desiredResult: number,
  options?: {
    startV?: number
    clampV?: {min?: number; max?: number}
    maxIter?: number
    tol?: number
    initialLearningRate?: number
  }
): T & {delta: number; v: number} => {
  const {maxIter = 100, tol = 1e-5, startV = 0, initialLearningRate = 0.1} = options ?? {}
  // sorted results by absolute delta ascending
  let results: (T & {delta: number; v: number})[] = []
  while (results.length < maxIter && (results[0] == undefined || Math.abs(results[0]!.delta) > tol)) {
    let v: number | undefined = undefined
    if (results.length == 0) {
      v = startV
    } else if (results.length == 1) {
      v = results[0]!.v + results[0]!.delta * initialLearningRate
    } else {
      const a = results[0]!
      const b = results.filter(r => isOppositeSign(r.delta, a.delta))[0] ?? results[1]!
      if (b.delta == a.delta) break

      const vPerDelta = (b.v - a.v) / (b.delta - a.delta)
      v = clamp(b.v - b.delta * vPerDelta, options?.clampV ?? {})
    }
    if (v == undefined) {
      throw new Error('solveForV: v is undefined')
    }
    const r = fx(v)
    if (r.result == undefined) return results[0]!
    results = sortBy([{delta: desiredResult - r.result, ...r, v, i: results.length}, ...results], r =>
      Math.abs(r.delta)
    )
  }
  return results[0]!
}
